import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Copy, Share2, MessageCircle, Twitter, Ticket, RotateCcw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { RewardProgress } from './RewardProgress';
interface SuccessStateProps {
  userData: {
    name: string;
    email: string;
  };
  onReset: () => void;
}
export function SuccessState({
  userData,
  onReset
}: SuccessStateProps) {
  const [queuePosition] = useState(() => Math.floor(Math.random() * 500) + 1200);
  const [referrals, setReferrals] = useState(0);
  const shareCode = `KRAYA${userData.name.substring(0, 3).toUpperCase()}${Math.floor(Math.random() * 999)}`;
  const shareLink = `https://kraya.live/?ref=${shareCode}`;
  const shareText = `I just joined the waitlist for Kraya, India's #1 coffee social app! ☕ Use my link to skip the line:`;
  useEffect(() => {
    const duration = 3 * 1000;
    const end = Date.now() + duration;
    const frame = () => {
      const partyColors = ['#FFFC00', '#FF5E5B', '#00CECB', '#FF9F1C', '#9B5DE5', '#00BBF9', '#F15BB5'];
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: {
          x: 0
        },
        colors: partyColors
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: {
          x: 1
        },
        colors: partyColors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, []);
  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard!', {
      style: {
        background: '#000',
        color: '#fff',
        border: '2px solid #000',
        borderRadius: '16px',
        fontFamily: 'Fredoka, sans-serif'
      }
    });
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText(shareCode);
    toast.success('Invite code copied!', {
      style: {
        background: '#000',
        color: '#fff',
        border: '2px solid #000',
        borderRadius: '16px',
        fontFamily: 'Fredoka, sans-serif'
      }
    });
  };
  return <motion.div className="w-full max-w-md mx-auto flex flex-col items-center" initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.5,
    type: 'spring',
    bounce: 0.4
  }} data-id="element-165">
      <div className="bg-white border-4 border-black rounded-[2rem] p-8 w-full neo-shadow-lg relative overflow-hidden" data-id="element-166">
        {/* Decorative background element */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-kraya-yellow rounded-full border-4 border-black opacity-50 z-0" data-id="element-167"></div>

        <div className="relative z-10 flex flex-col items-center text-center" data-id="element-168">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.3,
          type: 'spring'
        }} className="w-16 h-16 bg-kraya-yellow rounded-2xl border-4 border-black flex items-center justify-center text-3xl mb-4 neo-shadow" data-id="element-169">
            👋
          </motion.div>

          <h2 className="text-3xl font-bold mb-2" data-id="element-170">
            You're in, {userData.name}!
          </h2>
          <p className="text-gray-600 font-medium mb-6" data-id="element-171">
            Here is your spot in line:
          </p>

          <div className="bg-black text-white px-8 py-4 rounded-2xl border-4 border-black w-full mb-8 neo-shadow transform -rotate-1" data-id="element-172">
            <div className="text-sm font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-173">
              Queue Position
            </div>
            <div className="text-5xl font-bold" data-id="element-174">
              #{queuePosition - referrals * 5}
            </div>
          </div>

          <div className="w-full bg-gray-50 border-4 border-black rounded-2xl p-5 mb-6 text-left" data-id="element-175">
            <h3 className="font-bold text-lg mb-1 flex items-center gap-2" data-id="element-176">
              <Ticket className="w-5 h-5" data-id="element-177" /> Your invite code
            </h3>
            <p className="text-sm text-gray-600 font-medium mb-4" data-id="element-178">
              This is your unique code. We'll email you each time a friend signs
              up with it — and bump you up the line.
            </p>

            <button type="button" onClick={handleCopyCode} className="w-full bg-kraya-yellow border-4 border-black rounded-xl px-4 py-3 mb-5 flex items-center justify-between neo-shadow hover:neo-shadow-lg transition-all group" data-id="element-179">
              <span className="font-mono font-bold text-lg tracking-widest" data-id="element-180">
                {shareCode}
              </span>
              <span className="flex items-center gap-1 text-xs font-bold uppercase opacity-70 group-hover:opacity-100" data-id="element-181">
                <Copy className="w-4 h-4" data-id="element-182" /> Copy
              </span>
            </button>

            <h3 className="font-bold text-lg mb-2 flex items-center gap-2" data-id="element-183">
              <Share2 className="w-5 h-5" data-id="element-184" /> Share your link
            </h3>
            <div className="flex gap-2 mb-4" data-id="element-185">
              <div className="flex-1 bg-white border-2 border-black rounded-xl px-3 py-3 font-mono text-sm truncate flex items-center" data-id="element-186">
                {shareLink}
              </div>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={handleCopy} className="bg-black text-white p-3 rounded-xl border-2 border-black flex items-center justify-center neo-shadow" data-id="element-187">
                <Copy className="w-5 h-5" data-id="element-188" />
              </motion.button>
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-189">
              <a href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareLink)}`} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-black font-bold py-3 px-4 rounded-xl border-2 border-black flex items-center justify-center gap-2 hover:neo-shadow transition-all" data-id="element-190">
                <MessageCircle className="w-5 h-5" data-id="element-191" /> WhatsApp
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareLink)}`} target="_blank" rel="noopener noreferrer" className="bg-white text-black font-bold py-3 px-4 rounded-xl border-2 border-black flex items-center justify-center gap-2 hover:neo-shadow transition-all" data-id="element-192">
                <Twitter className="w-5 h-5" data-id="element-193" /> Post
              </a>
            </div>
          </div>

          <RewardProgress referrals={referrals} data-id="element-194" />
        </div>
      </div>

      <div className="w-full mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-195">
        <motion.button type="button" onClick={onReset} whileHover={{
        y: -2
      }} whileTap={{
        y: 2,
        scale: 0.98
      }} className="bg-white text-black font-bold py-3.5 px-4 rounded-2xl border-4 border-black neo-shadow hover:neo-shadow-lg transition-all flex items-center justify-center gap-2" data-id="element-196">
          <RotateCcw className="w-5 h-5" aria-hidden="true" data-id="element-197" /> Submit another
          entry
        </motion.button>
        <motion.a href="https://kraya.xyz/" target="_blank" rel="noopener noreferrer" whileHover={{
        y: -2
      }} whileTap={{
        y: 2,
        scale: 0.98
      }} className="bg-black text-white font-bold py-3.5 px-4 rounded-2xl border-4 border-black neo-shadow hover:neo-shadow-lg transition-all flex items-center justify-center gap-2" data-id="element-198">
          Learn more about Kraya{' '}
          <ExternalLink className="w-5 h-5" aria-hidden="true" data-id="element-199" />
        </motion.a>
      </div>
    </motion.div>;
}